import { Card, CardContent, CardTitle, CardDescription } from "@/shared/ui/card";
import { MapPin, MoreVertical, Pencil, Plus, Trash } from "lucide-react";
import { ServiceLinesSection } from "@/domains/service-lines/components/ServiceLinesSection";
import { Address } from "@/shared/types/addressTypes";
import { useMemo } from "react";
import Dropdown from "@/shared/components/Dropdown";
import { ServiceLine } from "@/shared/types/serviceLineTypes";

interface AddressCardProps {
  address: Address;
  onManageAddress?: () => void;
  onDeleteAddress?: () => void;
  onAddAddressToOrganization?: () => void;
  onRemoveAddressFromOrganization?: () => void;
  onManageServiceLine?: (serviceLine: ServiceLine) => void;
}

const AddressCard = ({
  address,
  onManageAddress,
  onDeleteAddress,
  onAddAddressToOrganization,
  onRemoveAddressFromOrganization,
  onManageServiceLine,
}: AddressCardProps) => {
  const dropdownMenuItems = useMemo(() => {
    const items = [];
    if (onAddAddressToOrganization && address.organization === undefined) {
      items.push({
        label: 'Add to Organization',
        icon: Plus,
        variant: 'default',
        onClick: onAddAddressToOrganization,
      });
    }

    if (onManageAddress && (address.serviceLines?.length ?? 0) === 0) {
      items.push({
        label: 'Manage',
        icon: Pencil,
        variant: 'default',
        onClick: onManageAddress,
      });
    }

    if (onRemoveAddressFromOrganization && address.organization && (address.serviceLines?.length ?? 0) === 0) {
      items.push({
        label: 'Remove from Organization',
        icon: Trash,
        variant: 'destructive',
        onClick: onRemoveAddressFromOrganization,
      });
    }

    if (onDeleteAddress && address.organization === undefined && (address.serviceLines?.length ?? 0) === 0) {
      items.push({
        label: 'Delete',
        icon: Trash,
        variant: 'destructive',
        onClick: onDeleteAddress,
      });
    }
    return items;
  }, [address, onAddAddressToOrganization, onDeleteAddress, onManageAddress, onRemoveAddressFromOrganization]);

  return (
    <Card className="flex flex-col gap-4">
      <CardContent className="flex flex-col gap-4 p-4">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center border">
              <MapPin className="h-6 w-6" />
            </div>
            <div>
              <div className="flex flex-col items-start">
                <CardTitle className="text-lg">{address.metadata || address.formattedAddress}</CardTitle>
                <CardDescription className="text-sm">{address.formattedAddress}</CardDescription>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {dropdownMenuItems.length > 0 && (
            <Dropdown triggerIcon={MoreVertical} items={dropdownMenuItems} />
          )}
        </div>

        {/* Service Lines Section */}
        {address.serviceLines?.length > 0 && (
          <ServiceLinesSection serviceLines={address.serviceLines} onManageServiceLine={onManageServiceLine} />
        )}
      </CardContent>
    </Card>
  );
};

export default AddressCard; 